(() => {
  const moneyFormat = () => window.MinimogSettings?.money_format || "${{amount}}";
  const formatPrice = (cents) => {
    if (typeof window.formatMoney === "function") return window.formatMoney(cents, moneyFormat());
    return new Intl.NumberFormat(document.documentElement.lang || "en-US", {
      style: "currency",
      currency: window.Shopify?.currency?.active || "USD"
    }).format(cents / 100);
  };

  const packOptionIndex = (product) => product.options.findIndex((name) =>
    /quantity|pack|case|count|light[- ]?quantity/i.test(name)
  );

  const unitCount = (value) => {
    const label = String(value || "");
    if (/\beach\b|\bsingle\b/i.test(label)) return 1;
    const totalLightsMatch = label.replace(/,/g, "").match(/\b(\d+)\s+(?:individual\s+)?lights?\b/i);
    if (totalLightsMatch) return Number(totalLightsMatch[1]);
    const match = label.replace(/,/g, "").match(/\d+/);
    return match ? Number(match[0]) : 0;
  };

  const fillTemplate = (template, replacements) => Object.entries(replacements).reduce(
    (result, [key, value]) => result.replaceAll(`__${key}__`, value),
    template
  );

  const sameNonPackOptions = (candidate, current, packIndex) => candidate.options.every(
    (value, index) => index === packIndex || value === current.options[index]
  );

  const updateCaseUpgrade = (productId, product, currentVariant) => {
    const upgrade = document.querySelector(`[data-law-case-upgrade][data-product-id="${productId}"]`);
    if (!upgrade) return;

    const index = packOptionIndex(product);
    const currentCount = index >= 0 ? unitCount(currentVariant.options[index]) : 0;
    if (index < 0 || currentCount < 1) {
      upgrade.hidden = true;
      return;
    }

    const target = product.variants
      .filter((variant) => variant.available && sameNonPackOptions(variant, currentVariant, index))
      .map((variant) => ({ variant, count: unitCount(variant.options[index]) }))
      .filter((item) => item.count > currentCount)
      .map((item) => ({
        ...item,
        savings: Math.round((currentVariant.price / currentCount) * item.count - item.variant.price)
      }))
      .filter((item) => item.savings > 0)
      .sort((a, b) => {
        const aIsCase = /case/i.test(a.variant.options[index]);
        const bIsCase = /case/i.test(b.variant.options[index]);
        if (aIsCase !== bIsCase) return Number(bIsCase) - Number(aIsCase);
        return b.savings - a.savings;
      })[0];

    if (!target) {
      upgrade.hidden = true;
      return;
    }

    const targetUnitPrice = Math.round(target.variant.price / target.count);
    upgrade.querySelector("[data-law-case-details]").textContent = fillTemplate(upgrade.dataset.detailsTemplate, {
      COUNT: target.count.toLocaleString(),
      PRICE: formatPrice(targetUnitPrice)
    });
    upgrade.querySelector("[data-law-case-savings]").textContent = fillTemplate(upgrade.dataset.savingsTemplate, {
      SAVINGS: formatPrice(target.savings)
    });

    const button = upgrade.querySelector("[data-law-case-button]");
    button.textContent = fillTemplate(upgrade.dataset.buttonTemplate, {
      QUANTITY: target.variant.options[index]
    });
    button.onclick = () => {
      const picker = document.querySelector(`[data-variant-picker][data-product-id="${productId}"]`);
      const field = picker?.querySelector(`[data-picker-field][data-option-name="${CSS.escape(product.options[index])}"]`);
      const radio = field?.querySelector(`input[value="${CSS.escape(target.variant.options[index])}"]`);
      const select = field?.querySelector("select");
      if (radio) radio.click();
      if (select) {
        select.value = target.variant.options[index];
        select.dispatchEvent(new Event("change", { bubbles: true }));
      }
      field?.scrollIntoView({ behavior: "smooth", block: "center" });
    };
    upgrade.hidden = false;
  };

  const updateShippingProgress = (productId, variant, quantity = 1) => {
    document.querySelectorAll(`[data-law-shipping-progress][data-product-id="${productId}"]`).forEach((progress) => {
      const cartTotal = Number(progress.dataset.cartTotal || 0);
      const selectedQuantity = Math.max(1, Number(quantity) || 1);
      const projectedTotal = cartTotal + variant.price * selectedQuantity;
      const threshold = Number(progress.dataset.threshold || 0);
      const remaining = Math.max(0, threshold - projectedTotal);
      progress.classList.toggle("is-qualified", remaining === 0);
      if (cartTotal >= threshold) {
        progress.textContent = progress.dataset.cartQualifiedMessage;
      } else if (variant.price * selectedQuantity >= threshold) {
        progress.textContent = progress.dataset.itemQualifiedMessage;
      } else if (projectedTotal >= threshold) {
        progress.textContent = progress.dataset.addingQualifiedMessage;
      } else {
        progress.textContent = fillTemplate(progress.dataset.remainingTemplate, { AMOUNT: formatPrice(remaining) });
      }
    });
  };

  const updateMainButtonText = (productId, product, variant) => {
    const buttonText = document.querySelector(`.m-product-form--main[data-product-id="${productId}"] .m-add-to-cart--text`);
    if (!buttonText || !variant.available) return;
    buttonText.textContent = window.MinimogStrings?.addToCart || "Add to cart";
  };

  const updateValueSummary = (productId, product, variant) => {
    document.querySelectorAll(`[data-law-value-summary][data-product-id="${productId}"]`).forEach((summary) => {
      const index = packOptionIndex(product);
      const label = summary.querySelector("[data-law-pack-label]");
      const unit = summary.querySelector("[data-law-unit-price]");
      const count = index >= 0 ? unitCount(variant.options[index]) : 0;

      const title = variant.public_title || variant.title;
      label.textContent = title === 'Default Title' ? '' : fillTemplate(summary.dataset.selectedTemplate, { VARIANT: title });
      if (count > 0) {
        unit.textContent = fillTemplate(summary.dataset.unitTemplate, { PRICE: formatPrice(Math.round(variant.price / count)) });
        unit.hidden = false;
      } else {
        unit.hidden = true;
      }
    });
  };

  const updateOrderSummary = (mainProduct, product, variant, quantity) => {
    const index = packOptionIndex(product);
    const summary = mainProduct.querySelector('[data-law-value-summary]');
    const output = mainProduct.querySelector('[data-law-order-summary]');
    const hint = mainProduct.querySelector('[data-law-quantity-help]');
    if (!summary) return;
    const qty = Math.max(1, Number(quantity) || 1);
    const pack = index >= 0 ? variant.options[index] : '';
    const count = pack ? unitCount(pack) : 0;
    if (output) {
      output.hidden = !pack;
      output.textContent = pack ? fillTemplate(summary.dataset.quantityTemplate, { QUANTITY: qty, PACK: pack }) : '';
      if (count > 0) output.textContent += ' · ' + fillTemplate(summary.dataset.totalTemplate, { COUNT: (qty * count).toLocaleString() });
    }
    if (hint) hint.hidden = !pack;
    mainProduct.querySelectorAll('[data-law-sticky-variant]').forEach(label => {
      const title = variant.public_title || variant.title;
      label.textContent = (qty > 1 ? qty + ' × ' : '') + (title === 'Default Title' ? product.title : title);
    });
  };

  const setupProductEnhancements = () => {
    document.querySelectorAll(".m-main-product").forEach((mainProduct) => {
      if (mainProduct.dataset.lawEnhancementsReady === "true") return;

      const productData = mainProduct.querySelector("#productData");
      if (!productData) return;

      let product;
      try {
        product = JSON.parse(productData.textContent);
      } catch (_error) {
        return;
      }

      const productId = String(product.id);
      const form = mainProduct.querySelector(`.m-product-form--main[data-product-id="${productId}"]`);
      const quantityInput = form?.querySelector('[name="quantity"]');
      const selectedId = Number(form?.querySelector('[name="id"]')?.value);
      const initialVariant = product.variants.find((variant) => variant.id === selectedId)
        || product.variants.find((variant) => variant.available)
        || product.variants[0];
      let activeVariant = initialVariant;

      const update = (variant) => {
        if (!variant) return;
        activeVariant = variant;
        updateCaseUpgrade(productId, product, variant);
        updateShippingProgress(productId, variant, quantityInput?.value);
        updateValueSummary(productId, product, variant);
        updateMainButtonText(productId, product, variant);
        updateOrderSummary(mainProduct, product, variant, quantityInput?.value);
      };

      update(initialVariant);
      quantityInput?.addEventListener("input", () => {
        updateShippingProgress(productId, activeVariant, quantityInput.value);
        updateOrderSummary(mainProduct, product, activeVariant, quantityInput.value);
      });
      quantityInput?.addEventListener("change", () => {
        updateShippingProgress(productId, activeVariant, quantityInput.value);
        updateOrderSummary(mainProduct, product, activeVariant, quantityInput.value);
      });
      window.MinimogEvents?.subscribe(`${productId}__VARIANT_CHANGE`, update);
      mainProduct.dataset.lawEnhancementsReady = "true";
    });
  };

  const setupMediaCounter = () => {
    document.querySelectorAll(".m-product-media--mobile").forEach((container) => {
      const gallery = container.querySelector("media-gallery");
      const current = container.querySelector("[data-law-media-current]");
      if (!gallery || !current) return;
      let attempts = 0;
      const timer = window.setInterval(() => {
        attempts += 1;
        if (gallery.slider) {
          const update = () => {
            current.textContent = String((gallery.slider.realIndex ?? gallery.slider.activeIndex ?? 0) + 1);
          };
          update();
          gallery.slider.on("slideChange", update);
          window.clearInterval(timer);
        } else if (attempts > 30) {
          window.clearInterval(timer);
        }
      }, 200);
    });
  };

  class LawStickyAtc extends HTMLElement {
    connectedCallback() {
      this.mainForm = document.querySelector(`.m-product-form--main[data-product-id="${this.dataset.productId}"]`);
      this.mainButton = this.mainForm?.querySelector(".m-add-to-cart");
      this.price = this.querySelector("[data-law-sticky-price]");
      this.variant = this.querySelector("[data-law-sticky-variant]");
      this.button = this.querySelector("[data-law-sticky-submit]");
      this.buttonText = this.querySelector("[data-law-sticky-button-text]");
      this.variants = JSON.parse(this.querySelector("[data-law-variants]").textContent);

      if (!this.mainForm || !this.mainButton) return;

      this.button.addEventListener("click", () => this.mainButton.click());
      this.observer = new IntersectionObserver(([entry]) => {
        this.hidden = entry.isIntersecting;
        document.documentElement.classList.toggle("stick-atc-show", !entry.isIntersecting);
      }, { threshold: 0.15 });
      this.observer.observe(this.mainButton);

      window.MinimogEvents?.subscribe(`${this.dataset.productId}__VARIANT_CHANGE`, (variant) => {
        if (variant) this.update(variant);
      });
    }

    update(variant) {
      this.price.textContent = formatPrice(variant.price);
      this.variant.textContent = variant.public_title || variant.title;
      this.button.disabled = !variant.available;
      this.buttonText.textContent = variant.available
        ? window.MinimogStrings.addToCart
        : window.MinimogStrings.soldOut;

      const product = JSON.parse(this.closest(".m-main-product").querySelector("#productData").textContent);
      updateCaseUpgrade(this.dataset.productId, product, variant);
      updateShippingProgress(
        this.dataset.productId,
        variant,
        this.mainForm?.querySelector('[name="quantity"]')?.value
      );
      updateValueSummary(this.dataset.productId, product, variant);
      updateMainButtonText(this.dataset.productId, product, variant);
      updateOrderSummary(this.closest('.m-main-product'), product, variant, this.mainForm?.querySelector('[name="quantity"]')?.value);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }
  }

  if (!customElements.get("law-sticky-atc")) customElements.define("law-sticky-atc", LawStickyAtc);

  window.addEventListener("load", () => {
    setupProductEnhancements();
    document.querySelectorAll("law-sticky-atc").forEach((bar) => {
      const selectedId = Number(bar.mainForm?.querySelector('[name="id"]')?.value);
      const variant = bar.variants?.find((item) => item.id === selectedId) || bar.variants?.[0];
      if (variant) bar.update(variant);
    });
    setupMediaCounter();
  });
})();
